import { Control, useController } from 'react-hook-form';

import { TextField, TextFieldProps } from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import useScreen from 'hooks/useScreen';
import useTranslates from 'utils/translate';

import sx from './styles';
import { ChangeEvent, ReactNode } from 'react';

interface FormInputProps {
  name: string;
  label?: string;
  control: Control;
  success?: boolean;
  withPopper?: boolean;
  endAdornment?: ReactNode;
  onInterceptValue?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
}

const mirrorValue = <T,>(value: T): T => value;

const FormInput = ({
  name,
  label,
  control,
  success,
  placeholder,
  endAdornment,
  onInterceptValue = mirrorValue,
  ...props
}: FormInputProps & TextFieldProps): JSX.Element => {
  const { isMobile } = useScreen();
  const { translate } = useTranslates();

  const {
    fieldState: { error },
    field: { ref, value = '', ...field },
  } = useController({
    name,
    control,
  });

  const onChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const interceptedEvent = onInterceptValue(event);
    field.onChange(interceptedEvent);
  };

  if (!name || !control) {
    return null;
  }

  const fieldPlaceholder = placeholder || label || '';

  return (
    <>
      <TextField
        {...props}
        {...field}
        value={value}
        onChange={onChange}
        error={!!error?.message}
        label={label}
        placeholder={fieldPlaceholder}
        ref={(el) => {
          ref(el);
        }}
        onBlur={(e) => {
          field.onBlur();
          props.onBlur?.(e);
        }}
        InputProps={{
          endAdornment: endAdornment || (success ? <CheckCircleOutlineIcon sx={sx.successIcon} /> : null),
          sx: ({ palette }) => ({
            'input::placeholder': {
              color: !isMobile && palette.text.secondary,
            },
          }),
        }}
        helperText={!!error?.message && translate(error.message)}
      />
    </>
  );
};

FormInput.defaultProps = {
  fullWidth: true,
};

export default FormInput;
